import {
  addTableColumns,
  addTableRows,
  removeTableColumns,
  removeTableRows,
} from '@/store/thunks/UpdateTable';
import { getCurrentThread } from '@/store/reducers/UIState';
import { getTableStateForThread } from '@/store/reducers/TableWidgetStore';
import {
  handleUIResetTable,
  TableToolPaper,
} from '@/actions/ProcessUIToolCalls';
import { showPageLevelError } from '@/components/shared';
import { TableColumn, TableRow } from '@/models/Table';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

import { FunctionComponent, ReactNode, useCallback, useState } from 'react';
import styled from 'styled-components';

const TABLE_ROWS: TableToolPaper[] = [
  { corpusId: 256868353, title: 'Citesee' }, // Citesee
  { corpusId: 257766269, title: 'Semantic Reader' }, // Semantic Reader
  { corpusId: 256194545, title: 'Semantic Scholar API' }, // Semantic Scholar API
  { corpusId: 215416146, title: 'S2ORC' }, // S2ORC
  { corpusId: 263671521, title: 'Rise of Open Science' }, // Rise of Open Science
  { corpusId: 258426468, title: 'S2abEL' }, // S2abEL
  { corpusId: 259224834, title: 'ARIES' }, // ARIES
  { corpusId: 102483154, title: 'Citation Intents' }, // Citation Intents
];

const TABLE_COLUMNS: TableColumn[] = [
  // Title
  {
    id: 'title',
    name: 'Title',
    description: 'The title of the paper',
    tools: ['s2Api'],
  },

  // Authors
  {
    id: 'authors',
    name: 'Authors',
    description: 'The authors of the paper',
    tools: ['s2Api'],
  },

  // Venue
  {
    id: 'venue',
    name: 'Venue',
    description: 'The venue of the paper',
    tools: ['s2Api'],
  },

  // Year
  {
    id: 'year',
    name: 'Year',
    description: 'The year the paper was published',
    tools: ['s2Api'],
  },

  // TL;DR
  {
    id: 'tldr',
    name: 'TL;DR',
    description: 'The TL;DR of the paper',
    tools: ['s2Api'],
  },

  // Abstract
  {
    id: 'abstract',
    name: 'Abstract',
    description: 'The abstract of the paper',
    tools: ['s2Api'],
  },

  // Model Name
  {
    id: 'modelName',
    name: 'Model Name',
    description: 'The name of the model primarily used in the paper',
    tools: ['paperQA'],
  },
];

interface TableRemoteProps {}

export const TableRemote: FunctionComponent<
  TableRemoteProps
> = (): ReactNode => {
  const dispatch = useAppDispatch();
  const currentThread = useAppSelector(getCurrentThread);
  const tableData = useAppSelector((state) =>
    getTableStateForThread(state, currentThread?.id),
  );

  const [isShown, setIsShown] = useState(false);

  const onClickRemoteIcon = useCallback(() => {
    setIsShown((prev) => !prev);
  }, [setIsShown]);

  const onClickAddRow = useCallback(() => {
    if (!currentThread) {
      return;
    }
    // HACK: Pull the next paper corpus id from the list of paper corpus ids
    const corpusIdsInRows = new Set(
      tableData?.rows.reduce((corpusIds: number[], row: TableRow) => {
        if (row.paperCorpusId) {
          corpusIds.push(row.paperCorpusId);
        }
        return corpusIds;
      }, []),
    );
    const newRow =
      TABLE_ROWS.find(({ corpusId }) => !corpusIdsInRows.has(corpusId)) ?? null;

    if (!newRow) {
      return;
    }

    dispatch(
      addTableRows({ currentThreadId: currentThread.id, papers: [newRow] }),
    );
  }, [currentThread, tableData]);

  const onClickRemoveRow = useCallback(() => {
    const rows = tableData?.rows ?? [];
    const lastRow = rows[rows.length - 1] ?? null;
    if (!currentThread || !lastRow || !tableData) {
      return;
    }

    dispatch(
      removeTableRows({
        currentThreadId: currentThread.id,
        rowIds: [lastRow.id],
      }),
    );
  }, [currentThread, tableData]);

  const onClickAddColumn = useCallback(() => {
    if (!currentThread || !tableData) {
      return;
    }
    // HACK: Pull the next column from the list of columns by name instead of id since thats autogenerated in the backend
    const columnNameInTable = new Set(
      (tableData?.columns ?? []).map((column) => column.name),
    );
    const newColumn = TABLE_COLUMNS.find(
      (column) => !columnNameInTable.has(column.name),
    );
    if (!newColumn) {
      return;
    }

    dispatch(
      addTableColumns({
        currentThreadId: currentThread.id,
        columns: [{ name: newColumn.name, description: newColumn.description }],
      }),
    );
  }, [currentThread, tableData]);

  const onClickRemoveColumn = useCallback(() => {
    if (!currentThread || !tableData) {
      return;
    }
    const columns = tableData?.columns ?? [];
    const lastColumn = columns[columns.length - 1] || null;

    if (!lastColumn) {
      return;
    }

    dispatch(
      removeTableColumns({
        currentThreadId: currentThread.id,
        columnIds: [lastColumn.id],
      }),
    );
  }, [currentThread, tableData]);

  const onClickResetTable = useCallback(() => {
    if (!currentThread) {
      return;
    }
    handleUIResetTable({
      currentThreadId: currentThread.id,
      dispatch,
    });
  }, [currentThread]);

  const onClickTriggerError = useCallback(() => {
    showPageLevelError({
      message: 'This is a fake error message',
    });
  }, []);

  if (!isShown) {
    return (
      <Positioning>
        <RemoteIconButton
          onClick={onClickRemoteIcon}
          title="Show remote control"
        />
      </Positioning>
    );
  }

  return (
    <Positioning>
      <Container>
        <Row>
          <Button onClick={onClickAddRow}>Add Row (to bottom)</Button>
          <Button onClick={onClickRemoveRow}>Remove Row (from bottom)</Button>
        </Row>
        <Row>
          <Button onClick={onClickAddColumn}>Add Column (at right)</Button>
          <Button onClick={onClickRemoveColumn}>
            Remove Column (from right)
          </Button>
        </Row>
        <Row>
          <Button onClick={onClickResetTable}>Reset Table</Button>
        </Row>
        <Row>
          <Button onClick={onClickTriggerError}>Trigger Fake Error</Button>
        </Row>
      </Container>
    </Positioning>
  );
};

const Positioning = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
`;

const Container = styled.div`
  outline: 1px dashed red;
  border-radius: 4px;
  padding: 8px;
`;

const RemoteIconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  opacity: 0.1;
  padding: 4px;
  transition: opacity 400ms ease-in-out;

  &:before {
    content: '📱';
    font-size: 24px;
  }

  &:hover {
    opacity: 1;
    transition-duration: 150ms;
  }
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px;
`;

const Button = styled.button``;
